import api from "@/utils/axios";

const USER_API_URL = `/users`;

export default {
  namespaced: true,
  state: {
    me: null,
    user: null,
    users: [],
    sessions: [],
    meta: {},
    loading: false,
    error: null,
  },
  mutations: {
    SET_ME(state, user) {
      state.me = user;
    },
    SET_USER(state, user) {
      state.user = user;
    },
    SET_USERS(state, users) {
      state.users = users;
    },
    SET_SESSIONS(state, sessions) {
      state.sessions = sessions;
    },
    SET_META(state, meta) {
      state.meta = meta;
    },
    SET_LOADING(state, status) {
      state.loading = status;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    CLEAR_USER(state) {
      state.user = null;
      state.sessions = [];
      state.error = null;
    },
    CLEAR_USERS(state) {
      state.users = [];
      state.meta = {};
      state.error = null;
    },
  },
  actions: {
    // Self-service
    async fetchMyUser({ commit }) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.get(`${USER_API_URL}/me/`, {
          successNotify: false,
          errorNotify: false,
        });
        commit("SET_ME", res.data.user);
        return res.data;
      } catch (error) {
        commit(
          "SET_ERROR",
          error.response?.data?.error || "Failed to fetch user"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async updateMyUser({ commit }, payload) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.patch(`${USER_API_URL}/me/`, payload);
        commit("SET_ME", res.data.user);
        return res.data;
      } catch (error) {
        commit("SET_ERROR", error.response?.data?.error || "Update failed");
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchMySessions({ commit }) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.get(`${USER_API_URL}/me/sessions/`, {
          successNotify: false,
          errorNotify: false,
        });
        commit("SET_SESSIONS", res.data.sessions);
        return res.data;
      } catch (error) {
        commit(
          "SET_ERROR",
          error.response?.data?.error || "Failed to fetch sessions"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    // Admin-only
    async fetchUser({ commit }, userId) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.get(`${USER_API_URL}/${userId}/`);
        commit("SET_USER", res.data.user);
        return res.data.user;
      } catch (error) {
        commit("SET_ERROR", error.response?.data?.error || "User not found");
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async fetchUserSessions({ commit }, userId) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.get(`${USER_API_URL}/${userId}/sessions/`);
        commit("SET_SESSIONS", res.data.sessions);
        return res.data.sessions;
      } catch (error) {
        commit(
          "SET_ERROR",
          error.response?.data?.error || "Failed to fetch sessions"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async filterUsers({ commit }, payload = {}) {
      commit("SET_LOADING", true);
      commit("SET_ERROR", null);
      try {
        const res = await api.post(`${USER_API_URL}/filter/`, payload);
        commit("SET_USERS", res.data.data);
        commit("SET_META", res.data.meta);
        return res.data;
      } catch (error) {
        commit(
          "SET_ERROR",
          error.response?.data?.error || "Failed to fetch users"
        );
        return null;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async addNewUser({ commit }, payload) {
      commit("SET_ERROR", null);
      try {
        const res = await api.post(`${USER_API_URL}/new/`, payload);
        return res.data.user;
      } catch (error) {
        commit(
          "SET_ERROR",
          error.response?.data?.error || "User creation failed"
        );
        return null;
      }
    },

    async updateUser({ commit }, { userId, data }) {
      commit("SET_ERROR", null);
      try {
        const res = await api.patch(`${USER_API_URL}/${userId}/`, data);
        return res.data.user;
      } catch (error) {
        commit(
          "SET_ERROR",
          error.response?.data?.error || "User update failed"
        );
        return null;
      }
    },

    async deleteUser({ commit }, userId) {
      commit("SET_ERROR", null);
      try {
        const res = await api.delete(`${USER_API_URL}/${userId}/`);
        return res.data.message;
      } catch (error) {
        commit(
          "SET_ERROR",
          error.response?.data?.error || "User deletion failed"
        );
        return null;
      }
    },

    async deleteUserSessions({ commit }, userId) {
      commit("SET_ERROR", null);
      try {
        const res = await api.delete(`${USER_API_URL}/${userId}/sessions/`);
        return res.data.message;
      } catch (error) {
        commit(
          "SET_ERROR",
          error.response?.data?.error || "Failed to delete sessions"
        );
        return null;
      }
    },

    clearUser({ commit }) {
      commit("CLEAR_USER");
    },

    clearUsers({ commit }) {
      commit("CLEAR_USERS");
    },
  },
  getters: {
    myUser: (state) => state.me,
    user: (state) => state.user,
    users: (state) => state.users,
    userMeta: (state) => state.meta,
    userSessions: (state) => state.sessions,
    userError: (state) => state.error,
    userLoading: (state) => state.loading,
  },
};
